import React, { useEffect, useState } from 'react';
import AgGrid from '@components/AgGrid/AgGrid';
import CircularProgress from '@material-ui/core/CircularProgress';
import TablePagination from '@material-ui/core/TablePagination';
import { useDispatch } from 'react-redux';
import { fetchPendingNotificationRecords } from '../../actions/global';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import Breadcrumbs from '@material-ui/core/Breadcrumbs';
import Typography from '@material-ui/core/Typography';
import { LinkCellRenderer } from '@trueprodigy/components';

const PendingRequestsDisplay = ({ setSelectedPending }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ results: [], recordCount: 0 });
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await dispatch(
        fetchPendingNotificationRecords({
          params: {
            page,
            pageSize: rowsPerPage,
          },
        }),
      );
      if (response?.results) {
        setData(response);
      }
    } catch (error) {
      console.error('Error fetching pending notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage]);

  const buildColumnDefs = () => [
    {
      headerName: 'Type',
      field: 'edrType',
    },
    {
      headerName: 'Request ID',
      field: 'requestID',
      cellRenderer: ({ data }) => {
        const url =
          data.edrType === 'A'
            ? `/appraisal/electronicDeliveryRequestAgent/${data.requestID}`
            : data.edrType === 'O'
            ? `/appraisal/electronicDeliveryRequestTaxpayer/${data.requestID}`
            : '#';
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Link to={url} target="_blank">
              {data.requestID}
            </Link>
          </div>
        );
      },
    },
    {
      headerName: 'Notification Type',
      field: 'processDisplay',
    },
    {
      headerName: '# Properties',
      field: 'propertyCount',
      width: 100,
    },
    {
      headerName: 'PID',
      field: 'pID',
      cellRenderer: ({ data }) => {
        if (data.pID === 'N/A') {
          return <span>{data.pID}</span>;
        }
        return (
          <span
            style={{ color: 'blue', cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() =>
              window.open(
                `/appraisal/property/${data.pID}/${data.pYear}/${data.pVersion}/${data.pRollCorr}`,
                '_blank'
              )
            }
          >
            {data.pID}
          </span>
        );
      },
    },
    {
      headerName: 'Event ID',
      field: 'eventID',
      width: 100,
      cellRenderer: LinkCellRenderer,
      cellRendererParams: {
        onClick: (_, data) => {
          window.open(`/events/edit/${data.eventID}`, '_blank');
        },
      },
    },
    {
      headerName: 'Send To',
      field: 'emailAddress',
    },
    {
      headerName: 'Create Dt',
      field: 'createDt',
      valueFormatter: (params) => {
        return params.value ? new Date(params.value).toLocaleDateString() : '';
      },
    },
    {
      headerName: 'Created By',
      field: 'createdBy',
    },
  ];

  return (
    <div>
      <TitleContainer>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
        >
          <Link
            to="#"
            onClick={() => setSelectedPending(false)}
            style={{ textDecoration: 'none' }}
          >
            Electronic Delivery Portal Notifications Runs
          </Link>
          <Typography color="textPrimary">
            Pending Portal Delivery Notifications
          </Typography>
        </Breadcrumbs>
      </TitleContainer>

      <TableContainer className="ag-theme-material">
        {loading ? (
          <div style={{ textAlign: 'center' }}>
            <CircularProgress />
          </div>
        ) : (
          <AgGrid
            defaultColDef={{ resizable: true, sortable: true }}
            columnDefs={buildColumnDefs()}
            rowData={data.results}
            onGridReady={(params) => {
              params.columnApi.autoSizeAllColumns();
            }}
          />
        )}
      </TableContainer>
      {data.recordCount > 0 && (
        <TablePagination
          component="div"
          count={data.recordCount}
          page={page - 1}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[20, 50, 100]}
          onPageChange={(event, newPage) => setPage(newPage + 1)}
          onRowsPerPageChange={({ target }) => {
            setRowsPerPage(parseInt(target.value, 10));
            setPage(1);
          }}
        />
      )}
    </div>
  );
};

PendingRequestsDisplay.propTypes = {
  setSelectedPending: PropTypes.func.isRequired,
};

const TableContainer = styled.div`
  padding: 20px;
  height: calc(100vh - 197px);
  background: #fff;
`;

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 20px 0;
  background: #fff;
`;

export default PendingRequestsDisplay;
